'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';

type OpenHomesImage = {
  src: string;
  alt: string;
  href?: string;
};

type Props = {
  images: OpenHomesImage[];
};

export default function OpenHomesPreview({ images }: Props) {
  const [activeImage, setActiveImage] = useState<OpenHomesImage | null>(null);
  const gridClassName =
    images.length === 1
      ? 'mx-auto grid max-w-3xl gap-4 grid-cols-1'
      : 'mx-auto grid max-w-6xl gap-4 grid-cols-1 md:grid-cols-2';

  useEffect(() => {
    if (!activeImage) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveImage(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeImage]);

  return (
    <>
      <div className={gridClassName}>
        {images.map((image) => {
          if (image.href) {
            return (
              <Link
                key={image.src}
                href={image.href}
                className="group block overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 transition-shadow duration-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/70"
              >
                <div className="relative h-[24rem] sm:h-[28rem] md:h-[30rem] overflow-hidden">
                  <Image
                    src={image.src}
                    alt={image.alt}
                    fill
                    className="object-contain transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
              </Link>
            );
          }

          return (
            <button
              key={image.src}
              type="button"
              className="group overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 transition-shadow duration-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/70"
              onClick={() => setActiveImage(image)}
            >
              <div className="relative h-[24rem] sm:h-[28rem] md:h-[30rem] overflow-hidden">
                <Image
                  src={image.src}
                  alt={image.alt}
                  fill
                  className="object-contain transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            </button>
          );
        })}
      </div>

      {activeImage ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Open homes image preview"
          onClick={() => setActiveImage(null)}
        >
          <div
            className="relative w-full max-w-[95vw] sm:max-w-5xl max-h-[90vh] overflow-hidden rounded-3xl bg-slate-900 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close image preview"
              className="absolute right-4 top-4 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow-lg transition hover:bg-white"
              onClick={() => setActiveImage(null)}
            >
              ×
            </button>
            <div className="relative h-[70vh] sm:h-[80vh] w-full bg-slate-950">
              <Image
                src={activeImage.src}
                alt={activeImage.alt}
                fill
                sizes="(min-width: 1024px) 80vw, 95vw"
                className="object-contain"
              />
            </div>
            <div className="bg-slate-900 px-6 py-4 text-center text-sm text-slate-100">
              {activeImage.alt}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
